import { useMemo } from "react";
import type { FileInputFormatIssue, FilePreparationOutcome } from "@rnavoyager/contracts";

import { SessionFileLink } from "./SessionFileReferences";
import type { TaskEvent } from "./types";

type FileAction = NonNullable<TaskEvent["file_action"]>;

export type FileEvidenceRow = { key: string; cursor: number } & (
  | { kind: "rejection"; functionName: string; issue: FileInputFormatIssue }
  | { kind: "preparation"; outcome: FilePreparationOutcome }
  | { kind: "consumer"; action: FileAction }
);

const CONSUMER_STATUS: Record<FileAction["status"], string> = {
  requested: "已请求，尚无完成回执",
  waiting_approval: "等待独立审批",
  succeeded: "已成功",
  failed: "已失败",
  cancelled: "已取消",
  timed_out: "已超时",
};

const MAX_EVIDENCE_ROWS = 64;

export function fileEvidence(
  events: readonly TaskEvent[],
  path: string,
): { rows: FileEvidenceRow[]; total: number } {
  const target = relativePath(path);
  const ordered = [...events].sort((left, right) => left.cursor - right.cursor);
  const rows = new Map<string, FileEvidenceRow>();
  const fileIds = new Set<string>();
  const digests = new Set<string>();

  for (const event of ordered) {
    const outcome = event.file_preparation;
    if (outcome) {
      const matchesSource = relativePath(outcome.source.path) === target;
      const matchesOutput = outcome.output ? relativePath(outcome.output.path) === target : false;
      if (!matchesSource && !matchesOutput) continue;
      fileIds.add(outcome.source.fileId);
      digests.add(outcome.source.digest);
      if (outcome.output) {
        fileIds.add(outcome.output.fileId);
        digests.add(outcome.output.digest);
      }
      const key = `preparation:${outcome.actionId}:${outcome.observationId}`;
      if (!rows.has(key))
        rows.set(key, { key, cursor: event.cursor, kind: "preparation", outcome });
      continue;
    }

    const action = event.file_action;
    if (!action) continue;
    const inputs = action.inputs.filter((input) => relativePath(input.path) === target);
    if (inputs.length === 0) continue;
    for (const input of inputs) {
      fileIds.add(input.fileId);
      digests.add(input.digest);
    }
    const key = `consumer:${action.actionId}`;
    const existing = rows.get(key);
    rows.set(key, {
      key,
      cursor: existing?.cursor ?? event.cursor,
      kind: "consumer",
      action: { ...action, inputs },
    });
  }

  for (const event of ordered) {
    const rejection = event.file_input_rejection;
    if (!rejection) continue;
    for (const issue of rejection.fileInputs ?? []) {
      if (!fileIds.has(issue.fileRef.fileId) && !digests.has(issue.digest)) continue;
      const key = JSON.stringify(["rejection", rejection.functionName, issue]);
      if (rows.has(key)) continue;
      rows.set(key, {
        key,
        cursor: event.cursor,
        kind: "rejection",
        functionName: rejection.functionName ?? "消费者",
        issue,
      });
    }
  }

  const all = [...rows.values()].sort((left, right) => left.cursor - right.cursor);
  return { rows: all.slice(-MAX_EVIDENCE_ROWS), total: all.length };
}

export function FileEvidencePanel({
  entry,
  events,
  sessionId,
}: {
  entry: { name: string; path: string };
  events: readonly TaskEvent[];
  sessionId: string;
}) {
  const evidence = useMemo(() => fileEvidence(events, entry.path), [entry.path, events]);
  if (evidence.rows.length === 0) return <p className="muted">该文件暂无来源与核验记录。</p>;
  return (
    <section aria-label="文件来源与核验" className="file-evidence">
      <p>格式核验成功不代表下游计算完成；每次实际动作均独立授权。</p>
      {evidence.total > evidence.rows.length && (
        <p>仅展示最近 {MAX_EVIDENCE_ROWS} 条记录，完整记录保留在任务事件中。</p>
      )}
      <ol>
        {evidence.rows.map((row) => (
          <li key={row.key}>
            {row.kind === "rejection" ? (
              <>
                <strong>文件输入被拒绝 · 消费者未执行</strong>
                <p>
                  {row.functionName}：{row.issue.actualFormat ?? "格式待确认"} →{" "}
                  {row.issue.acceptedFormats.join(" / ")}
                </p>
                <code title={row.issue.digest}>{row.issue.fileRef.fileId}</code>
                <small>
                  参数 {row.issue.pointer} [{row.issue.index}] · SHA-256{" "}
                  {row.issue.digest.slice(0, 12)}…
                </small>
              </>
            ) : row.kind === "preparation" ? (
              <>
                <strong>
                  {row.outcome.kind === "inspect" ? "格式检查" : "格式转换"} ·{" "}
                  {row.outcome.status === "succeeded" ? "已核验" : "失败，原文件保留"}
                </strong>
                <p>
                  {row.outcome.functionName}：{row.outcome.source.format ?? "格式待确认"} →{" "}
                  {row.outcome.output?.format ?? row.outcome.targetFormat ?? "未确认"}
                </p>
                <FileLabel file={row.outcome.source} sessionId={sessionId} />
                {row.outcome.output && (
                  <FileLabel file={row.outcome.output} sessionId={sessionId} />
                )}
                <details className="file-preparation-evidence">
                  <summary>核验标识</summary>
                  <small>动作 {row.outcome.actionId}</small>
                  <small>Observation {row.outcome.observationId}</small>
                  <small>函数合同 {row.outcome.functionSchemaDigest}</small>
                  <small>验证 profile {row.outcome.profileDigest}</small>
                  <small>结果摘要 {row.outcome.resultDigest}</small>
                </details>
              </>
            ) : (
              <>
                <strong>
                  {row.action.status === "requested" || row.action.status === "waiting_approval"
                    ? "关联文件动作"
                    : "文件消费"}{" "}
                  · {CONSUMER_STATUS[row.action.status]}
                </strong>
                <p>{row.action.functionName}</p>
                {row.action.inputs.slice(0, 8).map((input) => (
                  <FileLabel
                    key={`${input.pointer}:${input.index}`}
                    file={input}
                    sessionId={sessionId}
                  />
                ))}
                {row.action.inputs.length > 8 && (
                  <small>另有 {row.action.inputs.length - 8} 个关联输入，详见动作清单。</small>
                )}
                <small>动作 {row.action.actionId}</small>
              </>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function FileLabel({
  file,
  sessionId,
}: {
  file: FilePreparationOutcome["source"];
  sessionId: string;
}) {
  return (
    <div className="file-preparation-file">
      <SessionFileLink sessionId={sessionId} path={file.path} />
      <small title={file.digest}>
        {file.fileId} · {file.sizeBytes} 字节 · SHA-256 {file.digest.slice(0, 12)}…
      </small>
    </div>
  );
}

function relativePath(path: string): string {
  return path.startsWith("/project/") ? path.slice(9) : path.replace(/^\/+/, "");
}
